// Import Vue
import { createApp, watch } from "vue";

// Import Framework7
import Framework7 from "framework7/lite-bundle";

// Import Framework7-Vue Plugin
import Framework7Vue, { registerComponents } from "framework7-vue/bundle";
import { f7, f7ready } from "framework7-vue";

// Import Framework7 Styles
import "framework7/css/bundle";

// Skeleton loading elements (UX standard: skeletons for page-level lists,
// spinners only for inline actions)
import "skeleton-elements/css";

// Import Icons and App Custom Styles
import "../css/icons.css";
import "../css/app.css";
import "../css/framework7-keypad.min.css";

import Framework7Keypad from "framework7-plugin-keypad";
import dayjs from "./day.js";
import {
  setCurrencyResolver,
  getCurrency,
  formatMoney,
  toCurrency,
  toCurrencyExplicit,
} from "./utils/money.js";
import {
  formatDate,
  formatDateTime,
  formatTime,
  timeAgo,
  smartTimestamp,
  msgDate,
  toDateTime,
} from "./utils/datetime.js";
import { getInitial } from "./utils/initial.js";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";

import i18n from "./i18n.js";
import SecureLS from "secure-ls";
const storage_pwd = import.meta.env.VITE_APP_STR_PWD;
import _ from "lodash";
import VueApexCharts from "vue3-apexcharts";
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

import { setupPiniaPlugins } from "./pinia-stores/index.js";
import { useKeycloakStore } from "./pinia-stores/keycloak.js";
import { useAuthStore } from "./pinia-stores/auth.js";
import { useAccountStore } from "./pinia-stores/account.js";
import { useGroupsStore } from './pinia-stores/group.js'
import { useLocationStore } from "./pinia-stores/location.js";
import { useMessageStore } from "./pinia-stores/message.js";
import { initBrowserSplash, hideBrowserSplash } from "./browser-splash.js";
import {
  consumeSessionExpiry,
  sessionExpiredToast,
  sessionResumeToast,
} from "./session-expiry.js";
import {
  clearIntentUrl,
  getIntentUrl,
  isAtIntentUrl,
} from "./composables/useSessionGuard.js";

// Import App Component
import App from "../components/app.vue";
// Init Framework7-Vue Plugin
Framework7.use(Framework7Vue);
// install plugin
Framework7.use(Framework7Keypad);
import { defineCustomElements } from "@ionic/pwa-elements/loader";
defineCustomElements(window);

// Theme (system / light / dark) — applies body class before mount and
// re-applies after F7 init so the native status bar stays in sync.
import { initTheme, applyTheme } from "./composables/useTheme.js";
initTheme();
f7ready(() => applyTheme());

// Init App
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const app = createApp(App);

// Register Framework7 Vue components
const lcStorage = new SecureLS({
  encodingType: "aes",
  isCompression: true,
  encryptionSecret: `${storage_pwd}`,
});

app.config.globalProperties.$lcStorage = lcStorage;
window.__piniaLcStorage = lcStorage;
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
app.config.globalProperties.$filtersGetInitial = {
  getInitial,
};

app.use(pinia);
setupPiniaPlugins(pinia, app);

app.use(i18n);
app.use(dayjs);
app.use(_);
app.use(VueApexCharts);

/**
 * Currency lookup source for the central money formatter
 * (js/utils/money.js). Read lazily at call time: SecureLS cache first,
 * live location store as fallback — same precedence the old inline
 * helper used. locationStore is declared further below; by the time a
 * format call happens it is initialized.
 */
setCurrencyResolver((currencyCode) => {
  const currencyList = lcStorage.get("f7XquQcXg9d7ME");
  const currencies = currencyList ? currencyList : locationStore.currencyList;
  return (
    (Array.isArray(currencies) ? currencies : []).find(
      (currency) => currency.currencyCode === currencyCode,
    ) || null
  );
});

app.config.globalProperties.$filtersGetCountry = {
  getCountry(countryCode) {
    const countryList = lcStorage.get("L5ET4juMXyWmUgzY9");
    const country = countryList ? countryList : locationStore.country;
    return country.find(
      (countryItem) => countryItem.countryCode2 === countryCode,
    );
  },
};
app.config.globalProperties.$filtersGetCurrency = { getCurrency };

// ── Central formatters (js/utils/money.js & js/utils/datetime.js) ────
// Legacy filter objects delegate so every existing template keeps its
// exact output; new code should import from utils/ or use $money/$fmt.
app.config.globalProperties.$filtersToCurrency = { toCurrency };
app.config.globalProperties.$filtersToCurrency0 = {
  toCurrency: toCurrencyExplicit,
};
app.config.globalProperties.$money = formatMoney;
app.config.globalProperties.$fmt = {
  formatDate,
  formatDateTime,
  formatTime,
  timeAgo,
  smartTimestamp,
};

// Merged legacy date filters — previously split across two objects, and
// the second assignment silently orphaned toDateTime() which several
// pages still call.
app.config.globalProperties.$filtersToDate = { msgDate, toDateTime };
app.config.globalProperties.$filtersToTime = { toMsgTime: timeAgo };

let isStateLoaded = false;

app.config.globalProperties.$dayjs = dayjs;
app.provide("$dayjs", dayjs);

app.component("cropper", Cropper);
registerComponents(app);

const authStore = useAuthStore();
const accountStore = useAccountStore();
const groupStore = useGroupsStore();
const locationStore = useLocationStore();
const messageStore = useMessageStore();
const keycloakStore = useKeycloakStore();

app.config.globalProperties.$keycloak = keycloakStore.keycloak;
watch(
  () => keycloakStore.keycloak,
  (instance) => {
    app.config.globalProperties.$keycloak = instance;
    if (typeof window !== "undefined") {
      window._keycloak = instance;
    }
  },
  { immediate: true },
);

// ─── Post-auth State Loading ────────────────────────────────────────────────────

function withTimeout(promise, ms, message) {
  let handle;
  const timeout = new Promise((_, reject) => {
    handle = setTimeout(
      () => reject(new Error(message || "Operation timed out")),
      ms,
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(handle));
}

async function syncAuthState() {
  if (!keycloakStore.authenticated) {
    resetAuthState();
    return;
  }

  authStore.isAuthenticated_set(true);
  authStore.setToken(keycloakStore.token);
  authStore.setRefreshToken(keycloakStore.refreshToken);
  authStore.setKeycloak(keycloakStore.keycloak);

  if (!locationStore.emoneyApiList) {
    locationStore.getEmoneyApiList();
  }

  try {
    if (!keycloakStore.keycloakInstance.token) {
      throw new Error("No access token found in instance state.");
    }
    if (!authStore.profile && keycloakStore.authenticated) {
      const profile = await withTimeout(
        keycloakStore.loadUserInfo(),
        5000,
        "Keycloak loadUserInfo stuck pending",
      );
      authStore.setProfile(profile);

      if (profile && !accountStore.userAccount) {
        await accountStore.getUserAccount(profile);
      }
    }

    if (!authStore.user) {
      const user = keycloakStore.userInfo;
      authStore.setUser(user);
    }

    messageStore.ensureChatUser();
    messageStore.getUserGroups();
    groupStore.fetchGroups();
  } catch (err) {
    authStore.setProfile("");
    authStore.setUser("");
    console.error("Error loading user info/profile:", err);
  }

  getOrCreateAccount();
}

async function getOrCreateAccount() {
  if (!keycloakStore.authenticated) return;

  setTimeout(async () => {
    if (!accountStore.userAccount &&keycloakStore.authenticated) {
      try {
        const profile = keycloakStore.userInfo || (await keycloakStore.loadUserInfo());
        await accountStore.getUserAccount(profile);
      } catch (err) {
        console.error("Failed to create user avocash account:", err);
      }
    }
  }, 100);
}

function resetAuthState() {
  if (keycloakStore.authenticated) return;
  authStore.setProfile("");
  authStore.setUser("");
  authStore.isAuthenticated_set(false);
  authStore.setToken("");
  authStore.setRefreshToken("");
  accountStore.userAccount = "";
}

// ─── Watch for Keycloak auth changes ────────────────────────────────────────────

watch(
  () => keycloakStore.authenticated,
  (isAuth, wasAuth) => {
    if (isAuth && !wasAuth) {
      if (!isStateLoaded) {
        console.log("Syncing authentication state");
        isStateLoaded = true;
        syncAuthState();
      }
      const intentUrl = getIntentUrl();
      if (intentUrl && !isAtIntentUrl()) {
        clearIntentUrl();
        f7ready(() => {
          // The main view assigns its element asynchronously (f7-view's own
          // f7ready callback). Navigating before that crashes the router's
          // component loader ("reading 'children'"), so wait for router.el.
          const tryNavigate = (attempt = 0) => {
            const router = f7.views?.main?.router;
            if (router?.el) {
              console.log("Navigating to intentUrl:", intentUrl);
              const currentUrl = intentUrl==='/silent-check-sso.html' ? '/' : intentUrl;
              router.navigate(currentUrl);
            } else if (attempt < 40) {
              setTimeout(() => tryNavigate(attempt + 1), 50);
            }
          };
          tryNavigate();
        });
      } else if (isAtIntentUrl()) {
        sessionResumeToast();
      }
    } else if (!isAuth && wasAuth) {
      resetAuthState();
      isStateLoaded = false;
      if (consumeSessionExpiry()) {
        sessionExpiredToast();
      } else {
        clearIntentUrl();
      }
    }
  },
);

watch(
  () => keycloakStore.token,
  (newToken) => {
    if (newToken) {
      authStore.setToken(newToken);
      authStore.setRefreshToken(keycloakStore.refreshToken);
    }
  },
);

// ─── Bootstrap ──────────────────────────────────────────────────────────────────

async function bootstrap() {
  initBrowserSplash();

  try {
    await keycloakStore.initialize();
  } catch (error) {
    console.error("Keycloak initialization failed:", error);
  }

  hideBrowserSplash();
  app.mount("#app");
}

bootstrap();
